import { useState, useEffect, useRef } from 'react';

const ContactPage = ({ onClose, animationStage, onNavigate }) => {
  const [contentStage, setContentStage] = useState('entering');
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    company: '',
    project: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState(null);
  const [activeField, setActiveField] = useState(null);
  const pageRef = useRef(null);
  const canvasRef = useRef(null);
  const animationRef = useRef(null);

  // 3D Background Animation
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext('2d');
    let particles = [];
    let mouse = { x: 0, y: 0 };

    const resizeCanvas = () => {
      canvas.width = window.innerWidth;
      canvas.height = window.innerHeight;
    };

    class Particle {
      constructor() {
        this.x = Math.random() * canvas.width;
        this.y = Math.random() * canvas.height;
        this.size = Math.random() * 2 + 0.5;
        this.speedX = Math.random() * 0.5 - 0.25;
        this.speedY = Math.random() * 0.5 - 0.25;
        this.color = `rgba(100, 200, 255, ${Math.random() * 0.3 + 0.1})`;
      }

      update() {
        this.x += this.speedX;
        this.y += this.speedY;

        if (this.x > canvas.width) this.x = 0;
        else if (this.x < 0) this.x = canvas.width;
        if (this.y > canvas.height) this.y = 0;
        else if (this.y < 0) this.y = canvas.height;

        // Mouse interaction
        const dx = mouse.x - this.x;
        const dy = mouse.y - this.y;
        const distance = Math.sqrt(dx * dx + dy * dy);
        if (distance < 100) {
          this.speedX -= dx * 0.0005;
          this.speedY -= dy * 0.0005;
        }
      }

      draw() {
        ctx.fillStyle = this.color;
        ctx.beginPath();
        ctx.arc(this.x, this.y, this.size, 0, Math.PI * 2);
        ctx.fill();
      }
    }

    const initParticles = () => {
      particles = [];
      const particleCount = Math.min(100, Math.floor((canvas.width * canvas.height) / 15000));
      for (let i = 0; i < particleCount; i++) {
        particles.push(new Particle());
      }
    };

    const animate = () => {
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      
      // Draw connection lines
      ctx.strokeStyle = 'rgba(100, 200, 255, 0.1)';
      ctx.lineWidth = 0.5;
      
      for (let i = 0; i < particles.length; i++) {
        for (let j = i + 1; j < particles.length; j++) {
          const dx = particles[i].x - particles[j].x;
          const dy = particles[i].y - particles[j].y;
          const distance = Math.sqrt(dx * dx + dy * dy);
          
          if (distance < 100) {
            ctx.beginPath();
            ctx.moveTo(particles[i].x, particles[i].y);
            ctx.lineTo(particles[j].x, particles[j].y);
            ctx.stroke();
          }
        }
      }

      // Update and draw particles
      particles.forEach(particle => {
        particle.update();
        particle.draw();
      });

      animationRef.current = requestAnimationFrame(animate);
    };

    const handleMouseMove = (e) => {
      mouse.x = e.clientX;
      mouse.y = e.clientY;
    };

    // Initialize
    resizeCanvas();
    initParticles();
    animate();

    window.addEventListener('resize', resizeCanvas);
    window.addEventListener('mousemove', handleMouseMove);

    return () => {
      window.removeEventListener('resize', resizeCanvas);
      window.removeEventListener('mousemove', handleMouseMove);
      if (animationRef.current) {
        cancelAnimationFrame(animationRef.current);
      }
    };
  }, []);

  useEffect(() => {
    if (animationStage === 'entering') {
      setContentStage('entering');
      const timer = setTimeout(() => setContentStage('visible'), 100);
      return () => clearTimeout(timer);
    } else if (animationStage === 'exiting') {
      setContentStage('exiting');
    }
  }, [animationStage]);

  const handleInputChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleInputFocus = (fieldName) => {
    setActiveField(fieldName);
  };

  const handleInputBlur = () => {
    setActiveField(null);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus(null);

    try {
      // Simulate API call
      await new Promise(resolve => setTimeout(resolve, 2000));
      
      // Simulate successful submission
      setSubmitStatus('success');
      setFormData({
        name: '',
        email: '',
        company: '',
        project: '',
        message: ''
      });
      
      // Reset status after 5 seconds
      setTimeout(() => setSubmitStatus(null), 5000);
    } catch (error) {
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  const socialPlatforms = [
    { 
      name: 'X', 
      icon: (
        <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
          <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/>
        </svg>
      ), 
      color: 'hover:text-black hover:bg-white', 
      url: 'https://x.com/nixque',
      handle: '@nixque'
    },
    { 
      name: 'Instagram', 
      icon: (
        <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
          <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z"/>
        </svg>
      ), 
      color: 'hover:text-pink-500 hover:bg-gradient-to-r from-purple-500 to-pink-500', 
      url: 'https://instagram.com/nixque',
      handle: '@nixque'
    },
    { 
      name: 'Facebook', 
      icon: (
        <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
          <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/>
        </svg>
      ), 
      color: 'hover:text-blue-600 hover:bg-blue-600', 
      url: 'https://facebook.com/nixque',
      handle: '@nixque'
    },
    { 
      name: 'Reddit', 
      icon: (
        <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
          <path d="M12 0A12 12 0 0 0 0 12a12 12 0 0 0 12 12 12 12 0 0 0 12-12A12 12 0 0 0 12 0zm5.01 4.744c.688 0 1.25.561 1.25 1.249a1.25 1.25 0 0 1-2.498.056l-2.597-.547-.8 3.747c1.824.07 3.48.632 4.674 1.488.308-.309.73-.491 1.207-.491.968 0 1.754.786 1.754 1.754 0 .716-.435 1.333-1.01 1.614a3.111 3.111 0 0 1 .042.52c0 2.694-3.13 4.87-7.004 4.87-3.874 0-7.004-2.176-7.004-4.87 0-.183.015-.366.043-.534A1.748 1.748 0 0 1 4.028 12c0-.968.786-1.754 1.754-1.754.463 0 .898.196 1.207.49 1.207-.883 2.878-1.43 4.744-1.487l.885-4.182a.342.342 0 0 1 .14-.197.35.35 0 0  1 .238-.042l2.906.617a1.214 1.214 0 0 1 1.108-.701zM9.25 12C8.561 12 8 12.562 8 13.25c0 .687.561 1.248 1.25 1.248.687 0 1.248-.561 1.248-1.249 0-.688-.561-1.249-1.249-1.249zm5.5 0c-.687 0-1.248.561-1.248 1.25 0 .687.561 1.248 1.249 1.248.688 0 1.249-.561 1.249-1.249 0-.687-.562-1.249-1.25-1.249zm-5.466 3.99a.327.327 0 0 0-.231.094.33.33 0 0 0 0 .463c.842.842 2.484.913 2.961.913.477 0 2.105-.056 2.961-.913a.361.361 0 0 0 .029-.463.33.33 0 0 0-.464 0c-.547.533-1.684.73-2.512.73-.828 0-1.979-.196-2.512-.73a.326.326 0 0 0-.232-.095z"/>
        </svg>
      ), 
      color: 'hover:text-orange-500 hover:bg-orange-500', 
      url: 'https://reddit.com/r/nixque',
      handle: 'r/nixque'
    }
  ];

  const projectTypes = [
    'Web Application',
    'Mobile App',
    'Enterprise Software',
    'AI/ML Solution',
    'Cloud Migration',
    'Digital Transformation',
    'Tech Consultation',
    'Other'
  ];

  // Contact methods with clickable functionality
  const contactMethods = [
    {
      icon: "📧",
      title: "Email Us",
      value: "contact@nixque.com",
      description: "We'll respond within 24 hours",
      action: () => window.open('mailto:contact@nixque.com', '_blank')
    },
    {
      icon: "📞",
      title: "Call Us",
      value: "+254 742 606971",
      description: "Mon-Fri, 9AM-6PM EST",
      action: () => window.open('https://wa.me/254742606971', '_blank')
    },
    {
      icon: "📍",
      title: "Visit Us",
      value: "Nairobi | Kisumu, Kenya",
      description: "Schedule an office visit",
      action: null
    }
  ];

  return (
    <div 
      ref={pageRef}
      className="fixed inset-0 z-50 bg-slate-950 overflow-y-auto"
    >
      {/* Enhanced 3D Animated Background */}
      <div className="absolute inset-0 overflow-hidden">
        <canvas
          ref={canvasRef}
          className="absolute inset-0 w-full h-full"
        />
        {/* Gradient Overlays */}
        <div className="absolute inset-0 bg-gradient-to-br from-slate-900 via-purple-900/20 to-blue-900/30"></div>
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_center,_var(--tw-gradient-stops))] from-transparent via-slate-950/80 to-slate-950]"></div>
        
        {/* Animated Grid */}
        <div className="absolute inset-0 opacity-10">
          <div className="absolute inset-0 bg-[linear-gradient(rgba(255,255,255,0.05)_1px,transparent_1px),linear-gradient(90deg,rgba(255,255,255,0.05)_1px,transparent_1px)] bg-[size:50px_50px] [mask-image:radial-gradient(ellipse_80%_50%_at_50%_50%,black,transparent)] animate-grid-flow"></div>
        </div>

        {/* Floating Elements */}
        <div className="absolute inset-0">
          {[0, 1, 2, 3].map(i => (
            <div
              key={i}
              className="absolute rounded-full bg-cyan-500/10 animate-float-slow"
              style={{
                width: `${40 + i * 20}px`,
                height: `${40 + i * 20}px`,
                left: `${10 + i * 25}%`,
                top: `${20 + i * 15}%`,
                animationDelay: `${i * 3}s`,
                filter: 'blur(20px)'
              }}
            />
          ))}
        </div>
      </div>

      {/* Navigation */}
      <nav className="fixed top-0 left-0 right-0 z-50 border-b border-slate-800/50 backdrop-blur-xl bg-slate-950/70">
  <div className="max-w-7xl mx-auto px-6 py-4">
    <div className="flex justify-between items-center">
      <div className="flex items-center gap-3">
        <div className="w-2 h-2 rounded-full bg-cyan-400 animate-pulse"></div>
        <div className="flex items-center gap-3 text-white font-mono text-lg tracking-tight">
          <img 
            src="/icons/favicon-32x32.png" 
            alt="Nixque Icon" 
            className="w-6 h-6"
            onLoad={(e) => {
              console.log('✅ IMAGE LOADED SUCCESSFULLY!');
              console.log('Full URL:', e.target.src);
              console.log('Dimensions:', e.target.naturalWidth, 'x', e.target.naturalHeight);
            }}
            onError={(e) => {
              console.log('Attempted URL:', e.target.src);
              console.log('Trying fallback paths...');
              
              // Try different paths
              if (!e.target.dataset.retry1) {
                e.target.dataset.retry1 = 'true';
                console.log('Trying /favicon.ico');
                e.target.src = "/favicon.ico";
              } else if (!e.target.dataset.retry2) {
                e.target.dataset.retry2 = 'true';
                console.log('Trying /icons/favicon.ico');
                e.target.src = "/icons/favicon.ico";
              } else if (!e.target.dataset.retry3) {
                e.target.dataset.retry3 = 'true';
                console.log('Trying /icons/icon-32x32.png');
                e.target.src = "/icons/icon-32x32.png";
              } else {
                console.log('All paths failed, showing placeholder');
                e.target.style.display = 'none';
                const placeholder = document.createElement('div');
                placeholder.className = 'w-6 h-6 bg-cyan-400 rounded flex items-center justify-center border-2 border-green-500';
                placeholder.innerHTML = '<span class="text-[8px] font-bold text-gray-900">IMG</span>';
                e.target.parentNode.appendChild(placeholder);
              }
            }}
          />
          <span className="bg-gradient-to-r from-cyan-400 to-blue-400 bg-clip-text text-transparent">
            NIXQUE LAB
          </span>
        </div>
      </div>
      <button
        onClick={onClose}
        className="group px-6 py-3 rounded-xl border border-slate-700 text-white hover:bg-slate-800/50 transition-all duration-300 font-mono text-sm tracking-wide hover:border-cyan-400/50 hover:text-cyan-300 backdrop-blur-sm flex items-center gap-2"
      >
        <span>RETURN TO MAIN</span>
        <div className="group-hover:translate-x-1 transition-transform duration-200">
          ↗
        </div>
      </button>
    </div>
  </div>
</nav>

      {/* Main Content */}
      <div className="relative z-10 min-h-screen">
        <div className="max-w-7xl mx-auto px-6 py-16">
          {/* Enhanced Header Section */}
          <div className={`text-center mb-16 transition-all duration-1000 ${
            contentStage === 'entering' 
              ? 'opacity-0 transform translate-y-12 blur-sm' 
              : 'opacity-100 transform translate-y-0 blur-0'
          }`}>
            <div className="inline-flex items-center gap-3 px-4 py-2 rounded-full border border-slate-700/50 bg-slate-800/30 backdrop-blur-sm mb-6 group hover:border-cyan-400/30 transition-all duration-500 hover:scale-105">
              <div className="flex gap-1">
                {[0, 1, 2].map(i => (
                  <div key={i} className="w-1.5 h-1.5 rounded-full bg-cyan-400 animate-pulse" style={{ animationDelay: `${i * 0.2}s` }} />
                ))}
              </div>
              <span className="text-cyan-400 text-sm font-mono tracking-wide group-hover:text-cyan-300 transition-colors">
                LET'S BUILD TOMORROW
              </span>
            </div>
            
            <h1 className="text-4xl lg:text-6xl font-bold text-white mb-6 font-mono tracking-tighter relative">
              <span className="bg-gradient-to-br from-white via-cyan-100 to-blue-200 bg-clip-text text-transparent relative z-10">
                GET IN TOUCH
              </span>
              <div className="absolute inset-0 bg-gradient-to-r from-transparent via-cyan-400/10 to-transparent skew-x-12 blur-xl opacity-50"></div>
            </h1>
            
            <div className="flex justify-center mb-6">
              <div className="w-24 h-1 bg-gradient-to-r from-cyan-400 to-blue-400 rounded-full relative overflow-hidden">
                <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white to-transparent animate-shine"></div>
              </div>
            </div>
            
            <p className="text-lg text-slate-300 max-w-2xl mx-auto leading-relaxed">
              Ready to transform your vision into reality? Let's start a conversation about your next big project.
            </p>
          </div>

          {/* Submission Status */}
          {submitStatus && (
            <div className={`max-w-2xl mx-auto mb-8 p-4 rounded-xl backdrop-blur-sm border transition-all duration-500 ${
              submitStatus === 'success' 
                ? 'bg-green-500/10 border-green-400/30 text-green-400' 
                : 'bg-red-500/10 border-red-400/30 text-red-400'
            }`}>
              <div className="flex items-center gap-3">
                <div className={`w-5 h-5 rounded-full flex items-center justify-center ${
                  submitStatus === 'success' ? 'bg-green-400/20' : 'bg-red-400/20'
                }`}>
                  {submitStatus === 'success' ? '✓' : '✕'}
                </div>
                <div>
                  <h4 className="font-mono font-bold text-sm">
                    {submitStatus === 'success' ? 'MESSAGE SENT!' : 'SENDING FAILED'}
                  </h4>
                  <p className="text-xs mt-1">
                    {submitStatus === 'success' 
                      ? 'Thank you for your message! We\'ll get back to you within 24 hours.' 
                      : 'There was an error sending your message. Please try again.'
                    }
                  </p>
                </div>
              </div>
            </div>
          )}

          {/* Enhanced Contact Content Grid */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 max-w-6xl mx-auto">
            {/* Contact Information with Enhanced Animations */}
            <div className={`space-y-6 transition-all duration-1000 ${
              contentStage === 'entering' 
                ? 'opacity-0 transform -translate-x-12' 
                : 'opacity-100 transform translate-x-0'
            }`}>
              <div>
                <h2 className="text-2xl font-bold text-white mb-6 font-mono bg-gradient-to-r from-cyan-400 to-blue-400 bg-clip-text text-transparent">
                  CONTACT INFO
                </h2>
                
                {/* Enhanced Contact Methods */}
                <div className="space-y-4">
                  {contactMethods.map((method, index) => (
                    <EnhancedContactMethod 
                      key={method.title}
                      {...method}
                      delay={`${index * 200}ms`}
                    />
                  ))}
                </div>
              </div>

              {/* Enhanced Social Media */}
              <div>
                <h3 className="text-lg font-bold text-white mb-4 font-mono">FOLLOW OUR JOURNEY</h3>
                <div className="grid grid-cols-2 gap-3">
                  {socialPlatforms.map((platform, index) => (
                    <EnhancedSocialLink 
                      key={platform.name}
                      platform={platform}
                      index={index}
                    />
                  ))}
                </div>
              </div>

              {/* Trust Indicators */}
              <div className="p-4 rounded-xl border border-slate-800 bg-slate-900/30 backdrop-blur-sm">
                <h4 className="text-white font-mono text-xs mb-3 flex items-center gap-2">
                  <div className="w-1.5 h-1.5 rounded-full bg-cyan-400 animate-pulse"></div>
                  WHY PARTNER WITH US
                </h4>
                <div className="space-y-2 text-slate-400 text-xs">
                  <div className="flex items-center gap-2">
                    <div className="w-1 h-1 rounded-full bg-cyan-400 flex-shrink-0"></div>
                    <span>48-hour response guarantee</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <div className="w-1 h-1 rounded-full bg-cyan-400 flex-shrink-0"></div>
                    <span>Free initial consultation</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <div className="w-1 h-1 rounded-full bg-cyan-400 flex-shrink-0"></div>
                    <span>NDA-ready discussions</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <div className="w-1 h-1 rounded-full bg-cyan-400 flex-shrink-0"></div>
                    <span>Proven track record with 50+ clients</span>
                  </div>
                </div>
              </div>
            </div>

            {/* Enhanced Contact Form */}
            <div className={`transition-all duration-1000 ${
              contentStage === 'entering' 
                ? 'opacity-0 transform translate-x-12' 
                : 'opacity-100 transform translate-x-0'
            }`}>
              <EnhancedContactForm 
                formData={formData}
                onChange={handleInputChange}
                onFocus={handleInputFocus}
                onBlur={handleInputBlur}
                onSubmit={handleSubmit}
                isSubmitting={isSubmitting}
                activeField={activeField}
                projectTypes={projectTypes}
              />
            </div>
          </div>

          {/* Quick Response Section */}
          <div className={`max-w-2xl mx-auto mt-16 text-center transition-all duration-1000 ${
            contentStage === 'entering' 
              ? 'opacity-0 transform translate-y-8' 
              : 'opacity-100 transform translate-y-0'
          }`}>
            <div className="p-6 rounded-2xl border border-slate-800 bg-slate-900/20 backdrop-blur-sm">
              <h3 className="text-lg font-bold text-white mb-3 font-mono">NEED URGENT SUPPORT?</h3>
              <p className="text-slate-400 text-sm mb-4">
                For critical issues or emergency support, contact our rapid response team
              </p>
              <button 
                onClick={() => window.open('https://wa.me/254788979327', '_blank')}
                className="px-6 py-2 border border-cyan-400/30 text-cyan-400 rounded-lg font-mono text-xs hover:bg-cyan-400/10 hover:border-cyan-400/50 transition-all duration-300"
              >
                EMERGENCY CONTACT →
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Footer Section with Clickable Navigation */}
      <Footer onNavigate={onNavigate} />

      <style jsx>{`
        @keyframes shine {
          0% { transform: translateX(-100%); }
          100% { transform: translateX(100%); }
        }
        @keyframes float-slow {
          0%, 100% { transform: translateY(0px) rotate(0deg); }
          50% { transform: translateY(-10px) rotate(180deg); }
        }
        @keyframes grid-flow {
          0% { transform: translateX(0) translateY(0); }
          100% { transform: translateX(-50px) translateY(-50px); }
        }
        .animate-shine {
          animation: shine 3s ease-in-out infinite;
        }
        .animate-float-slow {
          animation: float-slow 8s ease-in-out infinite;
        }
        .animate-grid-flow {
          animation: grid-flow 20s linear infinite;
        }
      `}</style>
    </div>
  );
};

// Enhanced Contact Method Component
const EnhancedContactMethod = ({ icon, title, value, description, delay, action }) => (
  <div 
    onClick={action}
    className={`flex items-start gap-3 p-4 rounded-xl border backdrop-blur-sm transition-all duration-500 group hover:scale-105 ${
      action 
        ? 'cursor-pointer border-slate-800 bg-slate-900/30 hover:border-cyan-400/30 hover:bg-slate-800/40' 
        : 'border-slate-800 bg-slate-900/30'
    }`}
    style={{ animationDelay: delay }}
  >
    <div className="text-xl group-hover:scale-110 transition-transform duration-300">{icon}</div>
    <div className="flex-1">
      <h4 className="text-white font-semibold text-sm mb-1 group-hover:text-cyan-300 transition-colors">{title}</h4>
      <p className="text-cyan-400 font-mono text-xs mb-1 group-hover:text-cyan-300 transition-colors">{value}</p>
      <p className="text-slate-400 text-xs group-hover:text-slate-300 transition-colors">{description}</p>
    </div>
    {action && (
      <div className="text-slate-400 group-hover:text-cyan-400 transition-colors">
        →
      </div>
    )}
  </div>
);

// Enhanced Social Link Component
const EnhancedSocialLink = ({ platform, index }) => (
  <a
    href={platform.url}
    target="_blank"
    rel="noopener noreferrer"
    className={`p-3 rounded-lg border border-slate-800 bg-slate-900/30 backdrop-blur-sm transition-all duration-300 group hover:scale-105 ${platform.color} hover:border-transparent`}
    style={{ animationDelay: `${index * 100 + 800}ms` }}
  >
    <div className="flex items-center gap-2">
      <div className="text-slate-400 group-hover:text-white transition-colors">
        {platform.icon}
      </div>
      <div className="text-left">
        <div className="text-slate-400 group-hover:text-white font-mono text-xs transition-colors">
          {platform.name}
        </div>
        <div className="text-slate-500 text-xs group-hover:text-slate-300 transition-colors">
          {platform.handle}
        </div>
      </div>
    </div>
  </a>
);

// Enhanced Contact Form Component
const EnhancedContactForm = ({ formData, onChange, onFocus, onBlur, onSubmit, isSubmitting, activeField, projectTypes }) => (
  <form onSubmit={onSubmit} className="space-y-6 backdrop-blur-sm bg-slate-900/20 p-6 rounded-2xl border border-slate-800/50">
    <h2 className="text-2xl font-bold text-white mb-6 font-mono bg-gradient-to-r from-cyan-400 to-blue-400 bg-clip-text text-transparent">
      SEND MESSAGE
    </h2>
    
    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
      <EnhancedInputField
        label="Full Name"
        name="name"
        value={formData.name}
        onChange={onChange}
        onFocus={onFocus}
        onBlur={onBlur}
        isActive={activeField === 'name'}
        required
      />
      <EnhancedInputField
        label="Email Address"
        name="email"
        type="email"
        value={formData.email}
        onChange={onChange}
        onFocus={onFocus}
        onBlur={onBlur}
        isActive={activeField === 'email'}
        required
      />
    </div>
    
    <EnhancedInputField
      label="Company"
      name="company"
      value={formData.company}
      onChange={onChange}
      onFocus={onFocus}
      onBlur={onBlur}
      isActive={activeField === 'company'}
    />
    
    <EnhancedSelectField
      label="Project Type"
      name="project"
      value={formData.project}
      onChange={onChange}
      onFocus={onFocus}
      onBlur={onBlur}
      isActive={activeField === 'project'}
      options={projectTypes}
    />
    
    <EnhancedTextArea
      label="Project Details"
      name="message"
      value={formData.message}
      onChange={onChange}
      onFocus={onFocus}
      onBlur={onBlur}
      isActive={activeField === 'message'}
      required
    />
    
    <button
      type="submit"
      disabled={isSubmitting}
      className="w-full py-3 bg-gradient-to-r from-cyan-500 to-blue-500 text-white rounded-lg font-mono font-bold tracking-wide hover:from-cyan-600 hover:to-blue-600 transition-all duration-300 transform hover:scale-105 shadow-xl hover:shadow-cyan-500/25 disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none group relative overflow-hidden text-sm"
    >
      <span className="relative z-10">
        {isSubmitting ? (
          <div className="flex items-center justify-center gap-2">
            <div className="w-3 h-3 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
            SENDING...
          </div>
        ) : (
          'SEND MESSAGE'
        )}
      </span>
      <div className="absolute inset-0 bg-gradient-to-r from-cyan-600 to-blue-600 opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
    </button>

    <div className="text-center">
      <p className="text-slate-500 text-xs">
        By submitting this form, you agree to our privacy policy and terms of service.
      </p>
    </div>
  </form>
);

// Enhanced Input Field Component
const EnhancedInputField = ({ label, name, type = 'text', value, onChange, onFocus, onBlur, isActive, required = false }) => (
  <div className="relative">
    <label className={`block text-slate-300 text-xs font-mono mb-2 transition-all duration-300 ${
      isActive ? 'text-cyan-400 scale-105' : ''
    }`}>
      {label} {required && '*'}
    </label>
    <input
      type={type}
      name={name}
      value={value}
      onChange={onChange}
      onFocus={() => onFocus(name)}
      onBlur={onBlur}
      required={required}
      className={`w-full px-3 py-3 rounded-lg border backdrop-blur-sm text-white placeholder-slate-500 focus:outline-none transition-all duration-300 text-sm ${
        isActive 
          ? 'border-cyan-400/50 bg-cyan-500/10 shadow-lg shadow-cyan-500/20' 
          : 'border-slate-800 bg-slate-900/30 hover:border-slate-700'
      }`}
      placeholder={`Enter your ${label.toLowerCase()}`}
    />
  </div>
);

// Enhanced Select Field Component
const EnhancedSelectField = ({ label, name, value, onChange, onFocus, onBlur, isActive, options }) => (
  <div className="relative">
    <label className={`block text-slate-300 text-xs font-mono mb-2 transition-all duration-300 ${
      isActive ? 'text-cyan-400 scale-105' : ''
    }`}>
      {label}
    </label>
    <select
      name={name}
      value={value}
      onChange={onChange}
      onFocus={() => onFocus(name)}
      onBlur={onBlur}
      className={`w-full px-3 py-3 rounded-lg border backdrop-blur-sm text-white focus:outline-none transition-all duration-300 appearance-none text-sm ${
        isActive 
          ? 'border-cyan-400/50 bg-cyan-500/10 shadow-lg shadow-cyan-500/20' 
          : 'border-slate-800 bg-slate-900/30 hover:border-slate-700'
      } ${!value ? 'text-slate-500' : 'text-white'}`}
    >
      <option value="">Select project type</option>
      {options.map(option => (
        <option key={option} value={option} className="bg-slate-900 text-white">
          {option}
        </option>
      ))}
    </select>
    <div className="absolute right-3 top-1/2 transform -translate-y-1/2 pointer-events-none">
      <svg className="w-3 h-3 text-slate-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
      </svg>
    </div>
  </div>
);

// Enhanced TextArea Component
const EnhancedTextArea = ({ label, name, value, onChange, onFocus, onBlur, isActive, required = false }) => (
  <div className="relative">
    <label className={`block text-slate-300 text-xs font-mono mb-2 transition-all duration-300 ${
      isActive ? 'text-cyan-400 scale-105' : ''
    }`}>
      {label} {required && '*'}
    </label>
    <textarea
      name={name}
      value={value}
      onChange={onChange}
      onFocus={() => onFocus(name)}
      onBlur={onBlur}
      rows={4}
      className={`w-full px-3 py-3 rounded-lg border backdrop-blur-sm text-white placeholder-slate-500 focus:outline-none transition-all duration-300 resize-none text-sm ${
        isActive 
          ? 'border-cyan-400/50 bg-cyan-500/10 shadow-lg shadow-cyan-500/20' 
          : 'border-slate-800 bg-slate-900/30 hover:border-slate-700'
      }`}
      placeholder="Tell us about your project goals, timeline, and requirements..."
      required={required}
    />
    
  </div>
);

// Footer Component with Clickable Navigation
const Footer = ({ onNavigate }) => {
  const [currentYear] = useState(new Date().getFullYear());

  const socialLinks = [
    {
      name: 'X',
      icon: (
        <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
          <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/>
        </svg>
      ),
      url: 'https://x.com/nixque'
    },
    {
      name: 'Instagram',
      icon: (
        <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
          <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z"/>
        </svg>
      ),
      url: 'https://instagram.com/nixque'
    },
    {
      name: 'Facebook',
      icon: (
        <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
          <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/>
        </svg>
      ),
      url: 'https://facebook.com/nixque'
    },
    {
      name: 'Reddit',
      icon: (
        <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
          <path d="M12 0A12 12 0 0 0 0 12a12 12 0 0 0 12 12 12 12 0 0 0 12-12A12 12 0 0 0 12 0zm5.01 4.744c.688 0 1.25.561 1.25 1.249a1.25 1.25 0 0 1-2.498.056l-2.597-.547-.8 3.747c1.824.07 3.48.632 4.674 1.488.308-.309.73-.491 1.207-.491.968 0 1.754.786 1.754 1.754 0 .716-.435 1.333-1.01 1.614a3.111 3.111 0 0 1 .042.52c0 2.694-3.13 4.87-7.004 4.87-3.874 0-7.004-2.176-7.004-4.87 0-.183.015-.366.043-.534A1.748 1.748 0 0 1 4.028 12c0-.968.786-1.754 1.754-1.754.463 0 .898.196 1.207.49 1.207-.883 2.878-1.43 4.744-1.487l.885-4.182a.342.342 0 0 1 .14-.197.35.35 0 0 1 .238-.042l2.906.617a1.214 1.214 0 0 1 1.108-.701zM9.25 12C8.561 12 8 12.562 8 13.25c0 .687.561 1.248 1.25 1.248.687 0 1.248-.561 1.248-1.249 0-.688-.561-1.249-1.249-1.249zm5.5 0c-.687 0-1.248.561-1.248 1.25 0 .687.561 1.248 1.249 1.248.688 0 1.249-.561 1.249-1.249 0-.687-.562-1.249-1.25-1.249zm-5.466 3.99a.327.327 0 0 0-.231.094.33.33 0 0 0 0 .463c.842.842 2.484.913 2.961.913.477 0 2.105-.056 2.961-.913a.361.361 0 0 0 .029-.463.33.33 0 0 0-.464 0c-.547.533-1.684.73-2.512.73-.828 0-1.979-.196-2.512-.73a.326.326 0 0 0-.232-.095z"/>
        </svg>
      ),
      url: 'https://reddit.com/r/nixque'
    }
  ];

  const quickLinks = [
    { name: 'What We Do', page: 'what-we-do' },
    { name: 'About Us', page: 'about' },
    { name: 'Articles', page: 'articles' },
    { name: 'Contact', page: 'contact' }
  ];

  const handleNavigation = (page) => {
    if (onNavigate) {
      onNavigate(page);
    }
  };

  return (
    <footer className="relative border-t border-slate-800 bg-slate-950/50 backdrop-blur-xl">
      {/* Background Gradient */}
      <div className="absolute inset-0 bg-gradient-to-b from-transparent to-slate-900/20 pointer-events-none"></div>
      
      <div className="relative z-10 max-w-7xl mx-auto px-6 py-12">
        {/* Main Footer Content */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-8 mb-8">
          
          {/* Brand Column */}
          <div className="md:col-span-2">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-2 h-2 rounded-full bg-cyan-400 animate-pulse"></div>
              <div className="text-white font-mono text-lg font-bold tracking-tight bg-gradient-to-r from-cyan-400 to-blue-400 bg-clip-text text-transparent">
                NIXQUE
              </div>
            </div>
            <p className="text-slate-400 text-sm leading-relaxed max-w-md mb-6">
              Building the future through innovative technology solutions. 
              We create digital experiences that transform businesses and empower growth.
            </p>
            
            {/* Social Links */}
            <div className="flex gap-3">
              {socialLinks.map((social) => (
                <a
                  key={social.name}
                  href={social.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="p-2 rounded-lg border border-slate-800 bg-slate-900/30 text-slate-400 hover:text-cyan-400 hover:border-cyan-400/30 hover:bg-cyan-400/10 transition-all duration-300"
                  aria-label={`Follow us on ${social.name}`}
                >
                  {social.icon}
                </a>
              ))}
            </div>
          </div>

          {/* Quick Links */}
          <div>
            <h3 className="text-white font-mono text-sm font-semibold mb-4 uppercase tracking-wider">
              Navigation
            </h3>
            <ul className="space-y-2">
              {quickLinks.map((link) => (
                <li key={link.name}>
                  <button
                    onClick={() => handleNavigation(link.page)}
                    className="text-slate-400 hover:text-cyan-400 text-sm transition-colors duration-300 font-mono hover:underline cursor-pointer"
                  >
                    {link.name}
                  </button>
                </li>
              ))}
            </ul>
          </div>

          {/* Contact Info */}
          <div>
            <h3 className="text-white font-mono text-sm font-semibold mb-4 uppercase tracking-wider">
              Connect
            </h3>
            <div className="space-y-2 text-sm">
              <a
                href="mailto:contact@nixque.com"
                className="block text-slate-400 hover:text-cyan-400 transition-colors duration-300 font-mono"
              >
                contact@nixque.com
              </a>
              <a
                href="tel:+15551234567"
                className="block text-slate-400 hover:text-cyan-400 transition-colors duration-300 font-mono"
              >
                +2547 42 606971
              </a>
              <p className="text-slate-400 font-mono">
                Fully Remote
              </p>
            </div>
          </div>
        </div>

        {/* Bottom Bar */}
        <div className="pt-8 border-t border-slate-800">
          <div className="flex flex-col md:flex-row justify-between items-center gap-4">
            {/* Copyright */}
            <div className="text-slate-500 text-sm font-mono">
              © {currentYear} NIXQUE LTD. All rights reserved.
            </div>

            {/* Legal Links */}
            <div className="flex gap-6 text-sm">
              <button
                onClick={() => handleNavigation('privacy')}
                className="text-slate-500 hover:text-slate-300 transition-colors duration-300 font-mono"
              >
                Privacy
              </button>
              <button
                onClick={() => handleNavigation('terms')}
                className="text-slate-500 hover:text-slate-300 transition-colors duration-300 font-mono"
              >
                Terms
              </button>
              <button
                onClick={() => handleNavigation('cookies')}
                className="text-slate-500 hover:text-slate-300 transition-colors duration-300 font-mono"
              >
                Cookies
              </button>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default ContactPage;